import { Node } from '../../../core/models/scene';
import { getGenogramPersonSymbolRect } from './genogramPersonSymbolBounds';

export type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

export interface SnapPoint { x: number; y: number }

export interface BestCandidate {
  id: string;
  side: AnchorSide;
  pt: SnapPoint;
  dist: number;
}

export interface SnapResult {
  best: BestCandidate | null;
  candidatesById: Map<string, Array<{ side: AnchorSide; pt: SnapPoint }>>;
}

const pickPositiveNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return undefined;
};

const resolveNodeRect = (n: Node): { x: number; y: number; w: number; h: number } => {
  const attrs = ((n as any).attrs ?? {}) as Record<string, unknown>;
  const w = pickPositiveNumber(
    (n as any).rectW,
    (n as any).w,
    (n as any).width,
    attrs.rectW,
    attrs.w,
    attrs.width,
  ) ?? ((n as any).type === 'image' ? 240 : 60);
  const h = pickPositiveNumber(
    (n as any).rectH,
    (n as any).h,
    (n as any).height,
    attrs.rectH,
    attrs.h,
    attrs.height,
  ) ?? ((n as any).type === 'image' ? 160 : 60);
  const x = pickPositiveNumber((n as any).rectW, attrs.rectW) !== undefined
    ? (((n as any).rectX ?? attrs.rectX ?? n.x) as number)
    : n.x;
  const y = pickPositiveNumber((n as any).rectH, attrs.rectH) !== undefined
    ? (((n as any).rectY ?? attrs.rectY ?? n.y) as number)
    : n.y;
  return getGenogramPersonSymbolRect(n, { x, y, w, h }) ?? { x, y, w, h };
};

export type MeasuredNodeBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type NodeWithRectAliases = Node & {
  rectX?: number;
  rectY?: number;
  rectW?: number;
  rectH?: number;
};

export const findConnectionGeometryNode = (
  nodeId: string,
  visibleNodes: readonly Node[],
  storeNodes: readonly Node[] = [],
): Node | undefined => (
  visibleNodes.find((node) => node.id === nodeId)
    ?? storeNodes.find((node) => node.id === nodeId)
);

export const applyMeasuredBoundsToNode = (
  node: Node,
  bounds: MeasuredNodeBounds | null | undefined,
): Node => {
  if (!bounds || !(bounds.width > 0) || !(bounds.height > 0)) {
    return node;
  }
  const next: NodeWithRectAliases = { ...node };
  delete next.rectX;
  delete next.rectY;
  delete next.rectW;
  delete next.rectH;
  next.x = bounds.x;
  next.y = bounds.y;
  next.w = bounds.width;
  next.h = bounds.height;
  next.width = bounds.width;
  next.height = bounds.height;
  next.rectX = bounds.x;
  next.rectY = bounds.y;
  next.rectW = bounds.width;
  next.rectH = bounds.height;
  next.attrs = {
    ...(node.attrs ?? {}),
    rectX: bounds.x,
    rectY: bounds.y,
    rectW: bounds.width,
    rectH: bounds.height,
  };
  return next;
};

export const resolveConnectionGeometryNode = (input: {
  nodeId: string;
  visibleNodes: readonly Node[];
  storeNodes?: readonly Node[];
  measuredBounds?: MeasuredNodeBounds | null;
}): Node | undefined => {
  const node = findConnectionGeometryNode(input.nodeId, input.visibleNodes, input.storeNodes ?? []);
  if (!node) return undefined;
  return applyMeasuredBoundsToNode(node, input.measuredBounds);
};

type NodeSnapGeometry = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  rot: number;
};

export const rotatePoint = (cx: number, cy: number, px: number, py: number, deg: number): SnapPoint => {
  const r = (deg || 0) * Math.PI / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { x: cx + px * cos - py * sin, y: cy + px * sin + py * cos };
};

export const invRotatePoint = (cx: number, cy: number, wx: number, wy: number, deg: number): SnapPoint => {
  const r = -(deg || 0) * Math.PI / 180;
  const dx = wx - cx, dy = wy - cy;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
};

function getNodeSnapGeometry(n: Node): NodeSnapGeometry {
  let { x, y, w, h } = resolveNodeRect(n);

  if ((n as any).type === 'freehand') {
    const pts = Array.isArray((n as any)?.attrs?.points) ? ((n as any).attrs.points as number[]) : [];
    if (pts.length >= 4) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < pts.length; i += 2) {
        const px = pts[i] as number;
        const py = pts[i + 1] as number;
        if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
      if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
        x = (n as any).x + minX;
        y = (n as any).y + minY;
        w = Math.max(1, maxX - minX);
        h = Math.max(1, maxY - minY);
      }
    }
  }

  const cx = x + w / 2;
  const cy = y + h / 2;
  const rot = ((n.attrs as any)?.rotation as number) || 0;

  return { x, y, w, h, cx, cy, rot };
}

export function getNodeAnchorCandidates(n: Node): Array<{ side: AnchorSide; pt: SnapPoint }> {
  const { w, h, cx, cy, rot } = getNodeSnapGeometry(n);
  return [
    { side: 'top', pt: rotatePoint(cx, cy, 0, -h / 2, rot) },
    { side: 'right', pt: rotatePoint(cx, cy, w / 2, 0, rot) },
    { side: 'bottom', pt: rotatePoint(cx, cy, 0, h / 2, rot) },
    { side: 'left', pt: rotatePoint(cx, cy, -w / 2, 0, rot) },
  ];
}

export function getClosestNodeAnchor(
  pointer: { x: number; y: number },
  node: Node,
): { side: AnchorSide; pt: SnapPoint; dist: number } | null {
  const anchors = getNodeAnchorCandidates(node);
  if (anchors.length === 0) return null;

  let best = anchors[0];
  let bestDist = Math.hypot(best.pt.x - pointer.x, best.pt.y - pointer.y);
  for (let i = 1; i < anchors.length; i++) {
    const dist = Math.hypot(anchors[i].pt.x - pointer.x, anchors[i].pt.y - pointer.y);
    if (dist < bestDist) {
      best = anchors[i];
      bestDist = dist;
    }
  }

  return { side: best.side, pt: best.pt, dist: bestDist };
}

export function computeConnectionSnap(
  pointer: { x: number; y: number },
  nodes: Node[],
  excludeNodeId: string | null,
  edgeThreshold = 28,
  anchorThreshold = 12,
): SnapResult {
  let best: BestCandidate | null = null;
  const candidatesById = new Map<string, Array<{ side: AnchorSide; pt: SnapPoint }>>();

  nodes.forEach((n) => {
    if (excludeNodeId && n.id === excludeNodeId) return;
    const { w, h, cx, cy, rot } = getNodeSnapGeometry(n);
    const anchors = getNodeAnchorCandidates(n);
    candidatesById.set(n.id, anchors);

    const isImageNode = (n as any).type === 'image';

    // Default projection: to rotated rectangle edges
    const local = invRotatePoint(cx, cy, pointer.x, pointer.y, rot);
    const halfW = w / 2, halfH = h / 2;
    const clampedX = Math.max(-halfW, Math.min(halfW, local.x));
    const clampedY = Math.max(-halfH, Math.min(halfH, local.y));
    const dTop = Math.abs(-halfH - local.y);
    const dRight = Math.abs(halfW - local.x);
    const dBottom = Math.abs(halfH - local.y);
    const dLeft = Math.abs(-halfW - local.x);
    const minD = Math.min(dTop, dRight, dBottom, dLeft);
    let edgeSide: AnchorSide = 'top';
    let projLocal = { x: clampedX, y: -halfH };
    if (minD === dRight) { edgeSide = 'right'; projLocal = { x: halfW, y: clampedY }; }
    else if (minD === dBottom) { edgeSide = 'bottom'; projLocal = { x: clampedX, y: halfH }; }
    else if (minD === dLeft) { edgeSide = 'left'; projLocal = { x: -halfW, y: clampedY }; }
    let projWorld = rotatePoint(cx, cy, projLocal.x, projLocal.y, rot);
    let edgeDist = Math.hypot(projWorld.x - pointer.x, projWorld.y - pointer.y);

    // Special case: freehand (polyline). Project to closest point on the path for better glue.
    if ((n as any).type === 'freehand') {
      const pts = Array.isArray((n as any)?.attrs?.points) ? ((n as any).attrs.points as number[]) : [];
      if (pts.length >= 4) {
        let bestPx = projWorld.x;
        let bestPy = projWorld.y;
        let bestDSq = edgeDist * edgeDist;
        const offsetX = (n as any).x || 0;
        const offsetY = (n as any).y || 0;
        for (let i = 0; i < pts.length - 2; i += 2) {
          const ax = offsetX + (pts[i] as number);
          const ay = offsetY + (pts[i + 1] as number);
          const bx = offsetX + (pts[i + 2] as number);
          const by = offsetY + (pts[i + 3] as number);
          // Project pointer onto segment AB
          const abx = bx - ax, aby = by - ay;
          const apx = pointer.x - ax, apy = pointer.y - ay;
          const abLenSq = abx * abx + aby * aby;
          const t = abLenSq > 0 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq)) : 0;
          const px = ax + t * abx;
          const py = ay + t * aby;
          const dx = px - pointer.x;
          const dy = py - pointer.y;
          const dsq = dx * dx + dy * dy;
          if (dsq < bestDSq) { bestDSq = dsq; bestPx = px; bestPy = py; }
        }
        projWorld = { x: bestPx, y: bestPy };
        edgeDist = Math.sqrt(bestDSq);
        // Pick a side based on vector from center to the hit point
        const vx = bestPx - cx;
        const vy = bestPy - cy;
        edgeSide = Math.abs(vx) > Math.abs(vy) ? (vx >= 0 ? 'right' : 'left') : (vy >= 0 ? 'bottom' : 'top');
      }
    }

    let bestAnchor = anchors[0];
    let bestAnchorDist = Math.hypot(bestAnchor.pt.x - pointer.x, bestAnchor.pt.y - pointer.y);
    for (let i = 1; i < anchors.length; i++) {
      const d = Math.hypot(anchors[i].pt.x - pointer.x, anchors[i].pt.y - pointer.y);
      if (d < bestAnchorDist) { bestAnchor = anchors[i]; bestAnchorDist = d; }
    }

    if (isImageNode) {
      // Image nodes should snap to the 4 side-center ports only.
      if (bestAnchorDist < edgeThreshold) {
        if (!best || bestAnchorDist < best.dist) {
          best = { id: n.id, side: bestAnchor.side, pt: bestAnchor.pt, dist: bestAnchorDist };
        }
      }
      return;
    }

    if (bestAnchorDist < anchorThreshold) {
      if (!best || bestAnchorDist < best.dist) best = { id: n.id, side: bestAnchor.side, pt: bestAnchor.pt, dist: bestAnchorDist };
    } else if (edgeDist < edgeThreshold) {
      if (!best || edgeDist < best.dist) best = { id: n.id, side: edgeSide, pt: projWorld, dist: edgeDist };
    }
  });

  return { best, candidatesById };
}
